import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { api, formatMAD, resolveMediaUrl } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import { useI18n } from "@/context/I18nContext";
import Seo from "@/components/Seo";

export default function Wishlist() {
    const { ids, remove } = useWishlist();
    const { t } = useI18n();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            if (ids.length === 0) {
                setProducts([]);
                setLoading(false);
                return;
            }
            try {
                const res = await api.post("/products/batch", { ids });
                if (!cancelled) setProducts(res.data);
            } catch (_) {
                if (!cancelled) setProducts([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ids]);

    return (
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-12 sm:py-16" data-testid="wishlist-page">
            <Seo title={t("wishlist.title")} />

            <div className="mb-10 sm:mb-14">
                <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                    {t("catalog.eyebrow")}
                </p>
                <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tighter">
                    {t("wishlist.title")}
                </h1>
                <p className="text-sm text-white/50 mt-3">
                    {products.length} {t("wishlist.items")}
                </p>
            </div>

            {loading ? (
                <p className="text-white/50">...</p>
            ) : products.length === 0 ? (
                <div className="border border-white/10 py-20 text-center">
                    <Heart size={40} strokeWidth={1} className="text-white/30 mx-auto mb-6" />
                    <p className="font-display text-2xl uppercase font-black tracking-tighter">
                        {t("wishlist.empty")}
                    </p>
                    <p className="text-sm text-white/50 mt-3">{t("wishlist.empty_desc")}</p>
                    <Link
                        to="/collection"
                        className="mt-8 inline-flex items-center gap-2 px-8 h-12 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/90 transition-colors"
                        data-testid="wishlist-empty-shop-btn"
                    >
                        {t("wishlist.explore")} <ArrowRight size={14} />
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {products.map((p) => (
                        <div key={p.id} className="relative group" data-testid={`wishlist-item-${p.id}`}>
                            <Link to={`/product/${p.id}`} className="block">
                                <div className="relative aspect-[4/5] bg-[#0f0f0f] overflow-hidden">
                                    {p.images?.[0] && (
                                        <img
                                            src={resolveMediaUrl(p.images[0])}
                                            alt={p.name}
                                            className="product-image w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="mt-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-white/40">
                                        {p.category === "tshirt" ? t("product.tshirt") : t("product.cap")}
                                    </p>
                                    <p className="font-display text-sm sm:text-base font-bold uppercase tracking-tight truncate mt-1">
                                        {p.name}
                                    </p>
                                    <p className="text-sm font-semibold mt-1">
                                        {formatMAD(p.price)}
                                    </p>
                                </div>
                            </Link>
                            <button
                                onClick={() => remove(p.id)}
                                className="absolute top-3 right-3 w-9 h-9 bg-black/80 backdrop-blur border border-white/20 hover:bg-white hover:text-black flex items-center justify-center transition-colors"
                                aria-label={t("wishlist.remove")}
                                data-testid={`wishlist-remove-${p.id}`}
                            >
                                <Heart size={16} strokeWidth={1.5} fill="currentColor" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
